/**
 * Address — сбор адреса регистрации (шаг Flow step="address"). Подсказки DaData → структурированный
 * адрес, сохраняется в user.address. Пропсы: initial?, onBack(), onNext(address).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { Card, FallbackButton, Screen } from "@/components/Screen";
import { Hero } from "@/components/Hero";
import { Note } from "@/components/Note";
import { ErrorText } from "@/components/ErrorText";
import { Field } from "@/components/Field";
import { addressApi, friendlyError, type AddressStruct, type AddressSuggestion } from "@/lib/api";

type Props = {
  initial?: AddressStruct;
  onBack: () => void;
  onNext: (address: AddressStruct) => void;
};

export function Address({ initial, onBack, onNext }: Props) {
  const [q, setQ] = useState(initial?.value ?? "");
  const [items, setItems] = useState<AddressSuggestion[]>([]);
  const [selected, setSelected] = useState<AddressStruct | null>(initial ?? null);
  const [hasHouse, setHasHouse] = useState<boolean>(!!initial?.address_line);
  const [postal, setPostal] = useState(initial?.postal_code ?? "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const skipNext = useRef(false);

  // дебаунс-подсказки DaData
  useEffect(() => {
    if (skipNext.current) {
      skipNext.current = false;
      return;
    }
    const query = q.trim();
    if (query.length < 3) {
      setItems([]);
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await addressApi.suggest(query);
        if (alive) setItems(r.suggestions);
      } catch {
        /* подсказки не критичны */
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);

  const pick = useCallback((s: AddressSuggestion) => {
    skipNext.current = true;
    setQ(s.value);
    setItems([]);
    setSelected({
      country: s.country ?? "Россия",
      city: s.city ?? "",
      address_line: s.address_line,
      postal_code: s.postal_code,
      value: s.value,
    });
    setHasHouse(s.has_house);
    setPostal(s.postal_code ?? "");
  }, []);

  const valid = !!selected && !!selected.city && !!selected.address_line;

  const submit = useCallback(async () => {
    if (!valid || !selected || loading) return;
    setLoading(true);
    setErr(null);
    try {
      const payload = { ...selected, postal_code: postal.trim() || null };
      const r = await addressApi.save(payload);
      onNext(r.address);
    } catch (e) {
      setErr(friendlyError(e, "Не удалось сохранить адрес. Попробуйте ещё раз."));
    } finally {
      setLoading(false);
    }
  }, [valid, selected, postal, loading, onNext]);

  useBackButton(onBack);
  useMainButton({
    text: loading ? "Сохраняем..." : "Продолжить",
    onClick: submit,
    active: valid,
    loading,
  });

  return (
    <Screen>
      <Hero
        icon="📍"
        title="Адрес регистрации"
        subtitle="Начните вводить адрес и выберите подходящий вариант из подсказок."
      />

      <div className="relative">
        <Field
          autoFocus
          placeholder="Город, улица, дом"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setSelected(null);
          }}
          spellCheck={false}
        />
        {items.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-white/10 bg-surface-raised shadow-lg">
            {items.map((s, i) => (
              <li key={`${s.value}-${i}`}>
                <button
                  onClick={() => pick(s)}
                  className="block w-full border-b border-white/5 px-4 py-2.5 text-left text-[14px] last:border-0 active:bg-white/5"
                >
                  {s.value}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {q && !selected && items.length === 0 && (
        <Note className="mt-3">Выберите адрес из выпадающих подсказок, чтобы продолжить.</Note>
      )}

      {selected && (
        <div className="mt-4 space-y-3">
          <Card>
            <div className="text-[13px] text-white/45">Выбранный адрес</div>
            <div className="mt-1 text-[15px]">{selected.value || `${selected.city}, ${selected.address_line}`}</div>
          </Card>
          {!hasHouse && (
            <Note variant="warning">
              Уточните номер дома — выберите более точный вариант из подсказок.
            </Note>
          )}
          <Field
            label="Индекс"
            inputMode="numeric"
            placeholder="Почтовый индекс"
            value={postal}
            onChange={(e) => setPostal(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
        </div>
      )}

      {err && <ErrorText className="mt-4">{err}</ErrorText>}

      <FallbackButton text="Продолжить" onClick={submit} disabled={!valid} loading={loading} />
    </Screen>
  );
}
